import React, { useState, useEffect, useRef, useCallback } from "react";
import timelineProfile from "./timeline-profile.jpg";
import { useTranslation } from "react-i18next";
import i18n from "../src/lang/i18n";
import "./TestTimeline.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY;

const extractYouTubeID = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const isMobile = () => {
  return /Mobi|Android|iPhone/i.test(navigator.userAgent);
};

const FanCam = ({ darkMode, toggleDarkMode, language, langbt }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef(null);
  const scrollTimeout = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventList, setEventList] = useState([]);
  const [isEventListOpen, setIsEventListOpen] = useState(false);

  const itemsPerPage = 5;

  const fetchData = useCallback(
    async (pageNum = 1, sortOrderParam = "ASC", eventTagId = "") => {
      setIsLoading(true);
      try {
        let url = `${API_BASE_URL}/fancam?page=${pageNum}&limit=${itemsPerPage}&sortBy=uploadDate:${sortOrderParam}`;
        if (eventTagId) {
          url += `&filter.eventTagId=$eq:${encodeURIComponent(eventTagId)}`;
        }

        const response = await fetch(url, {
          headers: API_KEY ? { "x-api-key": API_KEY } : {},
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const result = await response.json();

        if (pageNum === 1) {
          setItems(result.data || []);
        } else {
          setItems((prev) => [...prev, ...(result.data || [])]);
        }
        const current = result?.meta?.currentPage ?? pageNum;
        const total = result?.meta?.totalPages ?? current;
        setPage(current);
        setTotalPages(total);
        setHasMore(current < total);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(1, "ASC", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchEventList = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/event-tag`, {
          headers: API_KEY ? { "x-api-key": API_KEY } : {},
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const opts = Array.isArray(data)
          ? data.map((t) => ({ id: t.id, name: t.name }))
          : [];
        setEventList(opts);
      } catch (error) {
        console.error("Error loading event list:", error);
      }
    };
    fetchEventList();
  }, []);

  const loadMoreItems = async () => {
    if (isLoading || !hasMore) return;
    await fetchData(page + 1, "ASC", selectedEvent?.id ?? "");
  };

  const handleScroll = () => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    const debounceTime = isMobile() ? 100 : 150;
    scrollTimeout.current = setTimeout(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollThreshold = isMobile()
        ? documentHeight * 0.5
        : documentHeight - windowHeight - 100;
      if (scrollTop >= scrollThreshold) {
        loadMoreItems();
      }
    }, debounceTime);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, items, hasMore, page, selectedEvent]);

  const getYouTubeAppLink = (url) => {
    const videoId = extractYouTubeID(url);
    if (!videoId) return url;
    if (/Android/i.test(navigator.userAgent)) {
      return `intent://www.youtube.com/watch?v=${videoId}#Intent;package=com.google.android.youtube;scheme=https;end`;
    }
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const handleThumbnailClick = (url) => {
    const link = isMobile() ? getYouTubeAppLink(url) : url;
    window.open(link, "_blank");
  };

  const handleEventFilter = (event) => {
    const newEvent = selectedEvent?.id === event.id ? null : event;
    setSelectedEvent(newEvent);
    setIsEventListOpen(false);
    fetchData(1, "ASC", newEvent?.id ?? "");
  };

  const toggleEventList = () => {
    setIsEventListOpen(!isEventListOpen);
  };

  const clearEventFilter = (e) => {
    e.stopPropagation();
    setSelectedEvent(null);
    fetchData(1, "ASC", "");
  };

  return (
    <div className={`FanCam ${darkMode ? "dark-mode" : ""}`}>
      <h1>{language("FanCam")}</h1>
      <h3>{language("dev_memo_1")}</h3>
      <div class="lang-switch">
        <button
          className={`lang-btn ${i18n.language === "ko" ? "active" : ""}`}
          onClick={langbt.ko}
        >
          KR
        </button>
        <button
          className={`lang-btn ${i18n.language === "en" ? "active" : ""}`}
          onClick={langbt.en}
        >
          EN
        </button>
        <button
          className={`lang-btn ${i18n.language === "jp" ? "active" : ""}`}
          onClick={langbt.jp}
        >
          JP
        </button>
      </div>

      <div className="event-picker">
        <div className="event-list-header" onClick={toggleEventList}>
          <h3 className="event-list-title">
            <span className="control-desktop-label">{selectedEvent
              ? language(selectedEvent.name)
              : language("filterTitle")}</span>
            <span className="control-mobile-label">{language("categoryLabel")}: {selectedEvent ? language(selectedEvent.name) : language("categoryAll")}</span>
          </h3>
          <div className="header-controls">
            {selectedEvent && (
              <button
                className="clear-filter"
                onClick={clearEventFilter}
                aria-label="Clear filter"
              >
                ×
              </button>
            )}
            <span
              className={`event-list-toggle ${isEventListOpen ? "open" : ""}`}
            >
              ▼
            </span>
          </div>
        </div>
        <div className={`event-list-content ${isEventListOpen ? "open" : ""}`}>
          {eventList.map((event) => (
            <div
              key={event.id}
              className={`event-item ${
                selectedEvent?.id === event.id ? "selected" : ""
              }`}
              onClick={() => handleEventFilter(event)}
            >
              {language(event.name)}
            </div>
          ))}
        </div>
      </div>
      <div className="test-timeline">
        {items.map((item, index) => {
          const videoId = extractYouTubeID(item.url);
          const thumbnailUrl = videoId
            ? `https://img.youtube.com/vi/${videoId}/0.jpg`
            : "";
          return (
            <div
              className={`test-timeline-item ${
                index % 2 === 0 ? "left" : "right"
              }`}
              key={item.id ?? item._id ?? `${item.url}-${index}`}
            >
              <div className="test-timeline-circle">
                <img
                  src={timelineProfile}
                  alt="QWER Logo"
                  className="test-timeline-circle-img"
                />
              </div>
              <div className="test-timeline-card">
                {thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt={item.title}
                    className="thumbnail"
                    onClick={() => handleThumbnailClick(item.url)}
                  />
                )}
                <div className="test-timeline-title-container">
                  <h2>{item.title}</h2>
                </div>
                <div className="test-timeline-date">
                  {formatDate(item.uploadDate)}
                </div>
                {item.churl && (
                  <a
                    href={
                      isMobile() ? getYouTubeAppLink(item.churl) : item.churl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="channel-label"
                  >
                    {language("ch")}: {item.source}
                  </a>
                )}
                <a
                  href={isMobile() ? getYouTubeAppLink(item.url) : item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="test-timeline-link"
                >
                  {language("watch")}
                </a>
              </div>
            </div>
          );
        })}
        <div ref={loader} className="loader">
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>{language("loading")}</p>
            </div>
          )}
        </div>
      </div>
      <footer className="footer">{language("footer")}</footer>
    </div>
  );
};

export default FanCam;
